import React, { FC } from 'react';
import { BasicContainer, DividerWithLabel, FlexCol, FlexDynamic, FlexRow, H2Text } from '@minecraft.atoms';
import { CardCvvElement, CardMonthElement, CardNumberElement, CardYearElement } from '@recurly/react-recurly';
import { useTranslation } from '@blocs.i18n';
import { WindowWidthContext } from '@minecraft.utils';
import Icon from '@minecraft.icon';
import { Input, StyledRecurlyInput } from '../AddPaymentMethod';
import { ApplePay } from '../../ApplePay/ApplePay';
import { PayPal } from '../../PayPal';

const NameFields: FC = () => {
  const { t } = useTranslation();

  return (
    <FlexDynamic gap="4" direction="column" wrap="nowrap" alignItems="center" w="full" mdProps={{ direction: 'row' }}>
      <Input
        type="text"
        name="first_name"
        label={t('common:form.label.firstName')}
        required
        data-recurly="first_name"
        dataQaId="cc-firstname-container"
      />
      <Input
        type="text"
        name="last_name"
        label={t('common:form.label.lastName')}
        required
        data-recurly="last_name"
        dataQaId="cc-lastname-container"
      />
    </FlexDynamic>
  );
};

const CardNumberField: FC<{ showIcons?: boolean }> = ({ showIcons }) => {
  const { t } = useTranslation();

  return (
    <FlexRow data-qa-id="cc-number-container" gap="4" wrap="nowrap" alignItems="center" w="full">
      <StyledRecurlyInput
        name="number"
        label={t('common:form.label.cardNumber')}
        required
        RecurlyInput={CardNumberElement}
        inputProps={{ className: 'cn_w-full' }}
        EndSlotComponent={
          showIcons && (
            <FlexRow gap="2" wrap="nowrap">
              <Icon name="master-card" />
              <Icon name="visa" />
              <Icon name="amex" />
            </FlexRow>
          )
        }
      />
    </FlexRow>
  );
};

const ExpirationFields = () => {
  const { t } = useTranslation();

  return (
    <FlexRow data-qa-id="cc-expiration-container" gap="4" wrap="nowrap">
      <StyledRecurlyInput
        inputProps={{ dataQaId: 'cc-month-expiration-container' }}
        name="month"
        label={t('common:form.label.month')}
        required
        RecurlyInput={CardMonthElement}
      />
      <StyledRecurlyInput
        inputProps={{ dataQaId: 'cc-year-expiration-container' }}
        name="year"
        label={t('common:form.label.year')}
        required
        RecurlyInput={CardYearElement}
      />
    </FlexRow>
  );
};

const CvvAndZipFields: FC<{ showCvvIcons?: boolean }> = ({ showCvvIcons }) => {
  const { t } = useTranslation();

  return (
    <FlexRow gap="4" wrap="nowrap" alignItems="center" w="full">
      <BasicContainer w={showCvvIcons ? '2/3' : '1/2'}>
        <StyledRecurlyInput
          name="cvv"
          label={t('common:form.label.cvv')}
          inputProps={{ className: 'cn_w-full', dataQaId: 'cc-cvv-container' }}
          required
          RecurlyInput={CardCvvElement}
          EndSlotComponent={
            showCvvIcons && (
              <FlexRow wrap="nowrap">
                <Icon name="amex-cvv" size="xl" />
                <Icon name="visa-mc-cvv" size="xl" />
              </FlexRow>
            )
          }
        />
      </BasicContainer>
      <BasicContainer w={showCvvIcons ? '1/3' : '1/2'}>
        <Input
          type="text"
          name="postal_code"
          label={t(showCvvIcons ? 'common:form.label.zipShort' : 'common:form.label.zip')}
          required
          data-recurly="postal_code"
          dataQaId="cc-zipcode-container"
        />
      </BasicContainer>
    </FlexRow>
  );
};

const PaymentForm: FC<{ isMobile: boolean }> = ({ isMobile }) => {
  return (
    <FlexCol gap="5">
      <NameFields />
      <CardNumberField showIcons={!isMobile} />
      <ExpirationFields />
      <CvvAndZipFields showCvvIcons={!isMobile} />
    </FlexCol>
  );
};

export const AddPaymentSection: FC<{
  showApplePayButton?: boolean;
  onClickApplePay: () => void;
  showPayPal?: boolean;
  onClickPayPal: () => void;
}> = React.memo(({ showApplePayButton, onClickApplePay, showPayPal, onClickPayPal }) => {
  const isMobile = WindowWidthContext.useIsMobile();
  const { t } = useTranslation();

  return (
    <BasicContainer
      borderPlacement={isMobile ? 'none' : 'all'}
      borderRadius={isMobile ? 'none' : 'md'}
      className={isMobile ? '' : 'cn_atom_p-4 cn_atom_pb-8'}
    >
      <FlexCol gap="4">
        {!isMobile && (
          <BasicContainer w="full" mb="6" mt="3">
            <H2Text>{t('common:paywall.addPaymentMethod.title')}</H2Text>
          </BasicContainer>
        )}
        <ApplePay showApplePay={showApplePayButton} onClick={onClickApplePay} />
        <PayPal showPayPal={showPayPal} onClick={onClickPayPal} />
        {(showApplePayButton || showPayPal) && <DividerWithLabel label={t('common:billing.card.or')} />}
        <PaymentForm isMobile={isMobile} />
      </FlexCol>
    </BasicContainer>
  );
});
